import { toast } from "sonner";

import { aplicarErroresDeComentario } from "@/components/feedback/errores";
import { FormularioDeComentario } from "@/components/feedback/FormularioDeComentario";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useActualizarComentario } from "@/hooks/useFeedback";
import { getApiErrorMessage } from "@/lib/api";
import type { ComentarioDeFeedback, OpcionDeFeedback } from "@/lib/feedback";

interface EditarComentarioDialogProps {
  /** Comentario a editar; `null` cierra el diálogo. */
  comentario: ComentarioDeFeedback | null;
  modulos: OpcionDeFeedback[];
  tipos: OpcionDeFeedback[];
  onCerrar: () => void;
}

/**
 * Corregir un comentario ya publicado: módulo, tipo y texto.
 *
 * Solo lo abre quien puede (`puede_editar` de la fila), pero el permiso real
 * lo comprueba el servidor en cada request: acá no hay ninguna decisión de
 * autorización.
 *
 * El ESTADO no se toca desde acá — es triaje, tiene otro permiso y otro
 * control (ver `TarjetaDeComentario`).
 */
export function EditarComentarioDialog({
  comentario,
  modulos,
  tipos,
  onCerrar,
}: EditarComentarioDialogProps) {
  const actualizar = useActualizarComentario();

  if (!comentario) return null;

  // Pausada NO es en vuelo: sin red, TanStack deja la mutación `isPending`
  // sin haber enviado nada, y como el diálogo es modal y atrapa el foco,
  // bloquear el cierre solo por `isPending` encerraría al usuario hasta
  // recuperar conexión (mismo criterio que `EliminarItemDialog`).
  const enVuelo = actualizar.isPending && !actualizar.isPaused;

  return (
    <Dialog
      open
      onOpenChange={(abierto) => {
        if (!abierto && !enVuelo) onCerrar();
      }}
    >
      <DialogContent
        className="sm:max-w-[35rem]"
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Editar comentario</DialogTitle>
          <DialogDescription>
            Corrige lo que escribiste. Lo ve todo el equipo, así que el cambio queda a la
            vista de quien ya lo había leído.
          </DialogDescription>
        </DialogHeader>

        {/* `key` con el id: si se cierra un comentario y se abre otro sin
            desmontar el diálogo, sin esto react-hook-form conserva los
            `defaultValues` del primero y la edición arranca con el texto
            equivocado. */}
        <FormularioDeComentario
          key={comentario.id}
          modulos={modulos}
          tipos={tipos}
          valorInicial={{
            modulo: comentario.modulo,
            tipo: comentario.tipo,
            texto: comentario.texto,
          }}
          etiquetaEnvio="Guardar cambios"
          enviando={enVuelo}
          onCancelar={onCerrar}
          onEnviar={(valores, acciones) =>
            actualizar.mutate(
              { id: comentario.id, ...valores },
              {
                onSuccess: () => {
                  toast.success("Comentario actualizado.");
                  onCerrar();
                },
                onError: (error) => {
                  // Un 422 se pinta en el campo que lo causó; cualquier otra
                  // cosa —un 403 de "no es tuyo", un 500— no tiene campo
                  // donde escribirse y va al toast.
                  if (aplicarErroresDeComentario(error, acciones)) return;
                  toast.error(
                    getApiErrorMessage(
                      error,
                      "No pudimos guardar los cambios. Inténtalo de nuevo.",
                    ),
                  );
                },
              },
            )
          }
        />
      </DialogContent>
    </Dialog>
  );
}
